import { useState } from "react";
import { Download, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { exportToCSV } from "@/lib/exportUtils";
import { toast } from "sonner";

/**
 * Reusable export dialog shell.
 *
 * Props:
 *  title        - Dialog heading
 *  filename     - Base filename for the download (date is auto-appended)
 *  data         - Already-filtered array to export
 *  columns      - Column definitions [{ key, label, format? }]
 *  children     - Filter controls rendered inside the dialog
 *  recordLabel  - Noun used in the count label, e.g. "events" (default: "records")
 */
const ExportDialog = ({
  title,
  filename,
  data,
  columns,
  children,
  recordLabel = "records",
}) => {
  const [open, setOpen] = useState(false);

  const handleExport = () => {
    const success = exportToCSV(data, filename, columns);
    if (success) {
      toast.success(`Exported ${data.length} ${recordLabel} to CSV`);
      setOpen(false);
    } else {
      toast.error("No data to export — adjust your filters and try again");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
        >
          <FileDown size={16} className="sm:mr-1.5" />
          <span className="hidden sm:inline">Export CSV</span>
        </Button>
      </DialogTrigger>

      <DialogContent className="bg-white border-gray-200 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <Download size={18} className="text-gray-500" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Filter controls provided by the parent page */}
          {children}

          {/* Footer: count + actions */}
          <div className="pt-3 border-t border-gray-100 space-y-3">
            <p className="text-sm text-gray-500">
              {data.length > 0 ? (
                <>
                  <span className="font-semibold text-gray-900">{data.length}</span>{" "}
                  {recordLabel} will be exported
                </>
              ) : (
                <span className="text-amber-600">
                  No {recordLabel} match the current filters
                </span>
              )}
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="flex-1 border-gray-200"
              >
                Cancel
              </Button>
              <Button
                onClick={handleExport}
                disabled={data.length === 0}
                className="flex-1 bg-[#BFFF00] hover:bg-[#a6e600] text-black disabled:opacity-40"
              >
                <Download size={15} className="mr-1.5" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
